import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
import { Ref, computed, reactive, ref, watch } from 'vue';
import { Dating } from '../@types/dating';
import {
  AvailableFilters,
  BasicFilter,
  Filter,
  VocabularyFilter,
} from '../@types/filter';
import Layout, { isLayout } from '../@types/layout';
import Pagination from '../@types/pagination';
import Sort, { isSort } from '../@types/sort';
import { Vocabulary } from '../@types/vocabulary';
import VocabularyKind from '../@types/vocabularyKind';
import { createApi, executeGqlQuery } from './gql';
import setURLParams from './params';
import {
  dispatchWindowEvent,
  lang,
  lockScrolling,
  mqMedium,
  objectMap,
} from './utils';

const DELIMITER = '||';
const urlParams = new URLSearchParams(document.location.search);
let initialFullText;
let initialSort = Sort.RELEVANCE;
let initialLayout = Layout.NORMAL;
let initialFrom;
let initialTo;
let initialPaginationSkip = 0;
let initialOpenContent;
let initialColors: string[] = [];
let initialHasAudioVideo;
const initialVocabularies: any[] = [];

/**
 * Dynamic vocabularies query depending on URL params.
 */
const vocabulariesQuery = computed((): DocumentNode | null => {
  if (!initialVocabularies.length) {
    return null;
  }

  return gql`
    query vocabularies($lang: Lang) {
      ${initialVocabularies.map(
        (
          vocabulary
        ) => `${vocabulary.kind}: vocabulary(id: "${vocabulary.id}", kind: ${vocabulary.gqlKind}, lang: $lang) {
        id
        text
      }`
      )}
    }
  `;
});

export const getFilterProperty = (
  kind: string
): keyof AvailableFilters | null => {
  interface PropertyMap {
    PERSON: string;
    THEME: string;
    COLOR: string;
    DISTRICT: string;
    CLASSIFICATION: string;
    CLOCK_DRIVE: string;
    ICONCLASS: string;
    MATERIAL: string;
    TECHNIQUE: string;
    PLACE: string;
    COIN_MINT: string;
    COIN_TERRITORY: string;
  }

  const propertyMap: PropertyMap = {
    PERSON: 'people',
    THEME: 'themes',
    COLOR: 'colors',
    DISTRICT: 'districts',
    CLASSIFICATION: 'classifications',
    CLOCK_DRIVE: 'clockDrives',
    ICONCLASS: 'iconclasses',
    MATERIAL: 'materials',
    TECHNIQUE: 'techniques',
    PLACE: 'places',
    COIN_MINT: 'coinMints',
    COIN_TERRITORY: 'coinTerritories',
  };

  if (propertyMap[kind as keyof PropertyMap]) {
    return propertyMap[kind as keyof PropertyMap] as keyof AvailableFilters;
  }
  return null;
};

for (const [key, value] of urlParams.entries()) {
  Object.keys(VocabularyKind).forEach((kind) => {
    const filterProperty = getFilterProperty(kind);
    if (filterProperty === key && value) {
      initialVocabularies.push({
        kind: filterProperty,
        id: value,
        gqlKind: kind,
      });
    }
  });

  if (key === 'colors') {
    initialColors = value.split(DELIMITER);
  }
  if (key === 'sort' && isSort(value)) {
    initialSort = value;
  }
  if (key === 'skip') {
    initialPaginationSkip = parseInt(value, 10);
  }
  if (key === 'layout' && isLayout(value)) {
    initialLayout = value;
  }
  if (key === 'from') {
    initialFrom = parseInt(value, 10);
  }
  if (key === 'to') {
    initialTo = parseInt(value, 10);
  }
  if (key === 'fullText') {
    initialFullText = value;
  }
  if (key === 'openContent') {
    initialOpenContent = value === 'true';
  }
  if (key === 'hasAudioVideo') {
    initialHasAudioVideo = value === 'true';
  }
}

// Fetch vocabularies to get labels for filter summary.
if (vocabulariesQuery.value) {
  const api = createApi();
  executeGqlQuery(
    api,
    vocabulariesQuery.value,
    {
      lang: lang.toUpperCase(),
    },
    (result: any) => {
      Object.entries(result).forEach(
        ([kind, value]: [kind: string, value: any]) => {
          const filterProperty =
            vocabularyFilter[kind as keyof VocabularyFilter];
          if (filterProperty) {
            filterProperty.text = value.text;
          }
        }
      );
    }
  );
}

export const pagination: Pagination = reactive({
  pageSize: 30,
  skip: initialPaginationSkip,
});
export const layout = ref(initialLayout);
export const sort = ref(initialSort);

export const filterVisible = ref(false);

export const toggleFilter = (): void => {
  filterVisible.value = !filterVisible.value;

  if (!mqMedium.matches) {
    lockScrolling(filterVisible.value);
  }

  // Update teaser height when toggling detail search.
  dispatchWindowEvent('matchheights');
};

const defaultBasicFilter: BasicFilter = {
  fullText: initialFullText,
  openContent: initialOpenContent,
  hasAudioVideo: initialHasAudioVideo,
};

export const basicFilter: BasicFilter = reactive(defaultBasicFilter);

export const datingFilter: Ref<Dating | undefined> = ref(
  initialFrom || initialTo
    ? ({ from: initialFrom, to: initialTo } as Dating)
    : undefined
);

const defaultVocabularyFilter: VocabularyFilter = Object.keys(
  VocabularyKind
).reduce((accumulator, current) => {
  const kind = getFilterProperty(current);
  const vocabulary = initialVocabularies.find((voc) => voc.kind === kind);
  accumulator[kind as keyof VocabularyKind] = vocabulary;
  return accumulator;
}, {} as any);

export const vocabularyFilter: VocabularyFilter = reactive(
  defaultVocabularyFilter
);

const defaultColorFilter = initialColors;
export const colorFilter: Ref<string[]> = ref(defaultColorFilter);

export const filter: Readonly<Ref<Filter>> = computed(() => ({
  ...basicFilter,
  datings: datingFilter.value,
  colors: colorFilter.value,
  ...vocabularyFilter,
}));

export const computedFilter = computed(() =>
  Object.entries(vocabularyFilter).filter(([_key, value]) => value)
);

const defaultAvailableFilters: AvailableFilters = {
  people: [],
  datings: undefined,
  classifications: [],
  materials: [],
  techniques: [],
  themes: [],
  districts: [],
  iconclasses: [],
  clockDrives: [],
  coinMints: [],
  coinTerritories: [],
  colors: [],
};

export const availableFilters: AvailableFilters = reactive(
  defaultAvailableFilters
);

const fixDating = (dating: Dating | undefined): Dating | undefined => {
  if (
    !dating ||
    (dating && dating.from === undefined && dating.to === undefined)
  ) {
    return undefined;
  }

  return {
    from: dating.from !== undefined ? dating.from : -999999,
    to: dating.to !== undefined ? dating.to : new Date().getFullYear(),
  };
};

export const sanitizedFilter: Readonly<Ref<Filter>> = computed(() => {
  return {
    fullText: basicFilter.fullText || undefined,
    openContent: basicFilter.openContent === true ? true : null,
    hasAudioVideo: basicFilter.hasAudioVideo === true ? true : null,
    datings: fixDating(datingFilter.value),
    colors: colorFilter.value.length ? colorFilter.value : undefined,
    ...objectMap(vocabularyFilter, (item: Vocabulary) =>
      item ? item.id : undefined
    ),
  };
});

const search = computed(() => ({
  fullText: basicFilter.fullText || undefined,
  ...objectMap(vocabularyFilter, (item: Vocabulary) =>
    item ? item.id : undefined
  ),
  openContent: basicFilter.openContent,
  hasAudioVideo: basicFilter.hasAudioVideo,
  from: datingFilter.value?.from,
  to: datingFilter.value?.to,
  colors: colorFilter.value?.join(DELIMITER),
  skip: pagination.skip,
  sort: sort.value,
  layout: layout.value,
}));

watch(search, () => {
  const params = Object.entries(search.value).filter(
    ([_key, value]) => value
  ) as string[][];
  setURLParams(params);
});
